import React, { useEffect, useState } from "react";
import { Box, Text, Badge, Grid, Flex, Tooltip } from "@chakra-ui/react";
import { useAppContext } from "../../contexts/AppContext";
import {
  CircleDollarSign,
  Coins,
  NotepadText,
  Wallet,
  TrendingDown,
  TrendingUp,
} from "lucide-react";


// Utility function to format large numbers with 'M' for millions



const FundsKPIs = () => {
  const { data, formatMillions } = useAppContext();
  const [totalFunds, setTotalFunds] = useState(0);
  const [totalNAV, setTotalNAV] = useState(0);
  const [averageCommitment, setAverageCommitment] = useState(0);
  const [topStrategy, setTopStrategy] = useState("");

  // Previous month data for comparison (dummy data)
  const previousMonthData = {
    totalFunds: 17, // Mocked value for previous month
    totalNAV: 14500000, // Example previous month NAV value
    totalCommitment: 10200000, // Example previous month commitment value
  };

  useEffect(() => {
    if (data.length > 0) {
      // Calculate total funds
      setTotalFunds(data.length);
  
      // Calculate total NAV
      const totalNavValue = data.reduce((acc, fund) => {
        return acc + fund.navValue; // Directly sum the numeric values
      }, 0);
      setTotalNAV(totalNavValue);
  
      // Calculate average commitment
      const totalCommitment = data.reduce((acc, fund) => {
        return acc + fund.commitment; // Directly sum the numeric values
      }, 0);
      setAverageCommitment(totalCommitment / data.length);
  
      // Determine top investment strategy
      const strategyCounts = data.reduce((acc, fund) => {
        const strategy = fund.investmentStrategy.name;
        acc[strategy] = (acc[strategy] || 0) + 1;
        return acc;
      }, {});
  
      const topStrategy = Object.keys(strategyCounts).reduce((a, b) =>
        strategyCounts[a] > strategyCounts[b] ? a : b
      );
      setTopStrategy(topStrategy);
    }
  }, [data]);

  // Function to calculate percentage change
  const calculatePercentageChange = (currentValue, previousValue) => {
    if (previousValue === 0) return 0; // Avoid division by zero
    return (((currentValue - previousValue) / previousValue) * 100).toFixed(0);
  };

  // Calculate the percentage changes
  const totalFundsChange = calculatePercentageChange(
    totalFunds,
    previousMonthData.totalFunds
  );
  const totalNAVChange = calculatePercentageChange(
    totalNAV,
    previousMonthData.totalNAV
  );
  const averageCommitmentChange = calculatePercentageChange(
    averageCommitment,
    previousMonthData.totalCommitment
  );

  // Function to render trend icons based on changes
  const renderTrendIcon = (change) => {
    if (change > 0) {
      return <TrendingUp color="green" size={16} />;
    } else if (change < 0) {
      return <TrendingDown color="red" size={16} />;
    } else {
      return null;
    }
  };

  return (
    <Grid
      templateColumns={{ lg: "repeat(1, 1fr)", base: "repeat(1, 1fr)" }}
      justifyContent="space-between"
      w={"fit-content"}
      gap={{ lg: 8, base: 4 }}
      pb={{ lg: 16, base: 8 }}
      sx={{
        ".kpi": {
          display: "flex",
          flexDirection: "row",
          gap: 4,
          flex: 1,
          alignItems: "center",
        },
        span: {
          w: "fit-content",
        },
        ".icon": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          w: { lg: "42px", base: "36px" },
          h: { lg: "42px", base: "36px" },
          borderRadius: 8,
          p: 1,
          bg: "grey.700",
          overflow: "hidden",
        },
        ".icon .lucide": {
          color: "white",
        },
      }}>
      <Flex className="kpi">
        <Box className="icon">
          <Wallet size={24} />
        </Box>
        <Flex direction={"column"}>
          <Text>Total Funds Managed</Text>
          <Flex alignItems="center" gap={2}>
            <Badge fontSize="lg" colorScheme="purple">
              {totalFunds}
            </Badge>
            <Tooltip label={`Change from last month: ${totalFundsChange}%`}>
              <Flex alignItems="center" gap={1}>
                {renderTrendIcon(totalFundsChange)}
                <Text
                  fontSize="sm"
                  fontWeight={"600"}
                  color={totalFundsChange > 0 ? "green" : "red"}>
                  {totalFundsChange}%
                </Text>
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex className="kpi">
        <Box className="icon">
          <CircleDollarSign size={24} />
        </Box>
        <Flex direction={"column"}>
          <Text>Total NAV</Text>
          <Flex alignItems="center" gap={2}>
            <Badge fontSize="lg" colorScheme="blue">
                {formatMillions(totalNAV)}
            </Badge>
            <Tooltip
              label={`Previous month: $${previousMonthData.totalNAV.toLocaleString()}`}>
              <Flex alignItems="center" gap={1}>
                {renderTrendIcon(totalNAVChange)}
                <Text
                  fontSize="sm"
                  fontWeight={"600"}
                  color={totalNAVChange > 0 ? "green" : "red"}>
                  {totalNAVChange}%
                </Text>
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex className="kpi">
        <Box className="icon">
          <Coins size={24} />
        </Box>
        <Flex direction={"column"}>
          <Text>Average Commitment</Text>
          <Flex alignItems="center" gap={2}>
            <Badge fontSize="lg" colorScheme="green">
                {formatMillions(averageCommitment)}
            </Badge>
            <Tooltip
              label={`Previous month: $${previousMonthData.totalCommitment.toLocaleString()}`}>
              <Flex alignItems="center" gap={1}>
                {renderTrendIcon(averageCommitmentChange)}
                <Text
                  fontSize="sm"
                  fontWeight={"600"}
                  color={
                    averageCommitmentChange > 0 ? "green" : "red"
                  }>
                  {averageCommitmentChange}%
                </Text>
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex className="kpi">
        <Box className="icon">
          <NotepadText size={24} />
        </Box>
        <Flex direction={"column"}>
          <Text>Top Investment Strategy</Text>
          <Badge fontSize="lg" colorScheme="orange">
            {topStrategy}
          </Badge>
        </Flex>
      </Flex>
    </Grid>
  );
};

export default FundsKPIs;
